import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { Wordmark } from '@/components/generals/wordmark'

import { CONTACT, SOCIALS, TAGLINE } from '@/constants/site'
import { FOOTER_NAV, FOOTER_SERVICES, LEGAL_NAV } from '@/constants/navigation'

const HEADING = 'font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60'
const LINK = 'text-sm text-cream/85 transition-colors duration-300 hover:text-coral'

export const Footer: FC = (): ReactNode => {
    const socials = SOCIALS.filter((social) => social.url)

    return (
        <footer data-nav-theme='dark' className='relative overflow-hidden border-t border-cream/10 bg-charcoal text-cream'>
            <div className='mx-auto max-w-[1440px] px-5 pt-20 md:px-10 md:pt-24'>
                <div className='grid gap-14 md:grid-cols-12 md:gap-8'>
                    <div className='md:col-span-5 lg:col-span-4'>
                        <Link href='/' aria-label='BLITZ home' className='inline-block'>
                            <Wordmark className='text-5xl md:text-6xl' />
                        </Link>
                        <p className='mt-6 max-w-sm text-[15px] leading-relaxed text-cream/80'>
                            A creative marketing agency combining brand strategy, advertising and performance marketing to help ambitious businesses grow.
                        </p>
                        <p className='mt-6 flex items-center gap-3 font-heading font-expanded text-lg font-bold'>
                            <span aria-hidden='true' className='h-px w-8 bg-coral' />
                            {TAGLINE}
                        </p>
                    </div>

                    <nav aria-label='Footer' className='grid grid-cols-2 gap-10 sm:grid-cols-3 md:col-span-7 lg:col-span-8 lg:grid-cols-4'>
                        <div>
                            <p className={HEADING}>Navigation</p>
                            <ul className='mt-5 space-y-3'>
                                {FOOTER_NAV.map((link) => (
                                    <li key={link.href}>
                                        <Link href={link.href} className={LINK}>{link.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='lg:col-span-1'>
                            <p className={HEADING}>Services</p>
                            <ul className='mt-5 space-y-3'>
                                {FOOTER_SERVICES.map((link) => (
                                    <li key={link.href}>
                                        <Link href={link.href} className={LINK}>{link.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='col-span-2 sm:col-span-1 lg:col-span-2'>
                            <p className={HEADING}>Contact</p>
                            <ul className='mt-5 space-y-3'>
                                {CONTACT.email && (
                                    <li>
                                        <a href={`mailto:${CONTACT.email}`} className={`${LINK} break-all`}>{CONTACT.email}</a>
                                    </li>
                                )}
                                {CONTACT.phone && (
                                    <li>
                                        <a href={`tel:${CONTACT.phone.replace(/[^\d+]/g, '')}`} className={LINK}>{CONTACT.phone}</a>
                                    </li>
                                )}
                                {CONTACT.location && (
                                    <li className='text-sm text-cream/85'>{CONTACT.location}</li>
                                )}
                                <li>
                                    <Link href='/contact' className='inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:underline'>
                                        Start a project
                                        <ArrowUpRight aria-hidden='true' className='size-4' />
                                    </Link>
                                </li>
                            </ul>

                            {socials.length > 0 && (
                                <>
                                    <p className={`${HEADING} mt-10`}>Social</p>
                                    <ul className='mt-5 flex flex-wrap gap-x-6 gap-y-3'>
                                        {socials.map((social) => (
                                            <li key={social.label}>
                                                <a href={social.url} target='_blank' rel='noopener noreferrer' className={LINK}>
                                                    {social.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    </nav>
                </div>

                <p aria-hidden='true' className='text-outline-cream mt-20 select-none whitespace-nowrap font-heading font-wide text-[19vw] font-black uppercase leading-[0.8] tracking-[-0.04em] opacity-30 md:mt-28 2xl:text-[17rem]'>
                    Blitz
                </p>

                <div className='mt-6 flex flex-col gap-5 border-t border-cream/15 py-6 text-xs text-cream/70 md:flex-row md:items-center md:justify-between'>
                    <p>
                        <span suppressHydrationWarning>&copy; {new Date().getFullYear()}</span> BLITZ. All rights reserved.
                    </p>

                    <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                        {LEGAL_NAV.map((link) => (
                            <li key={link.href}>
                                <Link href={link.href} className='transition-colors hover:text-coral'>{link.label}</Link>
                            </li>
                        ))}
                    </ul>

                    <a href='#top' className='inline-flex items-center gap-2 self-start uppercase tracking-[0.14em] transition-colors hover:text-coral md:self-auto'>
                        Back to top
                        <ArrowUp aria-hidden='true' className='size-4' />
                    </a>
                </div>
            </div>
        </footer>
    )
}
